<template>
  <div>
    <div
      v-for="option in addOns"
      :key="option.id"
      class="option__wrapper border-r10 pa-4 pt-6 mt-5 d-xl-flex align-xl-start mb-5"
      :class="option.isAdded ? 'border-2' : ''"
    >
      <div class="d-flex left-panel__wrapper">
        <v-img
          :src="require('@/assets/images/' + option.image)"
          transition="scale-transition"
          height="104"
          width="72"
          contain
          :alt="option.type"
          :class="option.code"
        />
        <div
          class="ml-3 pr-0 pr-xl-10 main_content_wrapper"
          v-if="hasWhatsIncludedDialog"
        >
          <h4 class="font-18">
            {{ option.type }}
          </h4>
          <p class="mt-3">
            {{ option.description }}
          </p>
          <h4 class="font-16" v-if="!option.isAdded">
            <template v-if="!option.hideAnnualCost">
              <div v-if="option.code === 'ad'">
                From + {{ option.annualCost() | formatCurrency }} per month
              </div>
              <div v-else>
                + {{ option.annualCost() | formatCurrency }}
                {{ option.annualCostLabel || "per year (ex IPT)" }}
              </div>
            </template>
          </h4>
          <div class="d-flex text--success mb-4" v-else>
            <v-icon size="18" class="mr-1" color="#1d8649"
              >mdi-check-circle</v-icon
            >
            <h4 class="text-6">Added to the policy</h4>
          </div>
          <div class="mt-4 d-flex mb-2">
            <a
              class="accent--text font-weight-semi-bold text-cursor pl-1"
              color="#106175"
              href="javascript:"
              @click="handleShowDialog(option)"
              aria-label="What is included in this?"
              >What is included in this?</a
            >
          </div>
        </div>

        <div class="ml-3 pr-0 pr-xl-10" v-else>
          <h4 class="font-18">
            {{ option.type }}
            <BaseTooltip :text="option.tooltipText" icon="mdi-help-circle" />
          </h4>
          <p class="mt-3">
            {{ option.description }}
          </p>
          <h4 class="font-16" v-if="!option.isAdded">
            + {{ option.annualCost() | formatCurrency }} per year (ex IPT)
          </h4>
          <div class="d-flex text--success mb-4" v-else>
            <v-icon size="18" class="mr-1" color="#1d8649"
              >mdi-check-circle</v-icon
            >
            <h4 class="text-6">Added to the policy</h4>
          </div>
        </div>
      </div>

      <BaseButton
        :text="option.isAdded ? 'REMOVE' : 'ADD'"
        height="60"
        min-width="100"
        rounded
        depressed
        :type="option.isAdded ? 'text' : ''"
        :block="$vuetify.breakpoint.xl ? false : true"
        :color="option.isAdded ? '' : 'psorange'"
        :icon="option.isAdded ? 'mdi-minus-circle' : ''"
        :class="!option.isAdded ? 'font-weight-bold' : 'border-1 ml-lg-3'"
        class="font-20 py-6 mt-5 mt-xl-0"
        iconColor="var(--red-500)"
        @click="toggleOption(option)"
      />
    </div>

    <BaseDialog
      v-model="isShowWhatsIncludedDialogHE"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      :headerDivider="true"
      title="What is included in this?"
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedHomeEmergency
          @click:hideDialog="isShowWhatsIncludedDialogHE = false"
        />
      </template>
    </BaseDialog>

    <BaseDialog
      v-model="isShowWhatsIncludedDialogLE"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      :headerDivider="true"
      title="What is included in this?"
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedLegalExpenses
          @click:hideDialog="isShowWhatsIncludedDialogLE = false"
        />
      </template>
    </BaseDialog>

    <BaseDialog
      v-model="isShowWhatsIncludedDialogRP"
      windowClass="pa-0 pb-xl-0"
      headerClass="pa-6 mb-n1"
      :headerDivider="true"
      title="What is included in this?"
    >
      <template v-slot:content>
        <DialogContentWhatsIncludedRentProtection
          @click:hideDialog="isShowWhatsIncludedDialogRP = false"
        />
      </template>
    </BaseDialog>

    <DrawerRentProtectionLegalExpensesEligibilityCheck
      v-model="isShowEligibilityDrawer"
      @confirm="onEligibilityConfirmed"
      @cancel="onEligibilityCancelled"
    />
  </div>
</template>

<script>
import BaseButton from "@/components/BaseComponents/BaseButton.vue";
import BaseTooltip from "@/components/BaseComponents/BaseTooltip.vue";
import {
  HOME,
  LANDLORDS,
  TENANTS_CONTENTS,
} from "@/constants/questionset.constants";
import { convertToBool } from "@/common/utils";
import { ADD_ONS_ANNUAL_COST_LANDLORDS } from "@/constants/additional-cover-options";
import { DIALOG_MODULE, QUESTIONS_MODULE } from "@/store/types/module-types";
import { mapMutations, mapState } from "vuex";

export default {
  components: {
    BaseButton,
    BaseTooltip,
    DialogContentWhatsIncludedHomeEmergency: () =>
      import(
        "@/components/QuoteResult/landlords/DialogContentWhatsIncludedHomeEmergency.vue"
      ),
    DialogContentWhatsIncludedLegalExpenses: () =>
      import(
        "@/components/QuoteResult/landlords/DialogContentWhatsIncludedLegalExpenses.vue"
      ),
    DialogContentWhatsIncludedRentProtection: () =>
      import(
        "@/components/QuoteResult/landlords/DialogContentWhatsIncludedRentProtection.vue"
      ),
    BaseDialog: () => import("@/components/BaseComponents/BaseDialog.vue"),
    DrawerRentProtectionLegalExpensesEligibilityCheck: () =>
      import(
        "@/components/QuoteResult/landlords/DrawerRentProtectionLegalExpensesEligibilityCheck.vue"
      ),
  },
  props: {
    addOns: Array,
    productGroup: String,
    handleToggleExternal: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapState(QUESTIONS_MODULE, ["questionMap"]),
    hasWhatsIncludedDialog() {
      return [HOME, LANDLORDS, TENANTS_CONTENTS].includes(this.productGroup);
    },
  },
  data() {
    return {
      isShowWhatsIncludedDialogHE: false,
      isShowWhatsIncludedDialogLE: false,
      isShowWhatsIncludedDialogRP: false,
      LANDLORDS,
      isShowEligibilityDrawer: false,
      isEligibilityConfirmed: false,
      pendingOption: null,
      lastNewLetAnswer: null,
      legalExpenses: {},
      rentProtection: {},
      landlordsEmergency: {},
      accidentalDamage: {},
    };
  },
  watch: {
    addOns: {
      handler(addOns) {
        if (this.productGroup !== LANDLORDS) return;
        addOns.forEach((addOn) => {
          if (addOn.code === "he")
            this.landlordsEmergency.Value = addOn.isAdded;

          if (addOn.code === "le") {
            this.legalExpenses.Value = addOn.isAdded;

            //Apply discount to LE if RP is selected
            if (addOns.some((addOn) => addOn.isAdded && addOn.code === "rp")) {
              addOn.annualCost = ADD_ONS_ANNUAL_COST_LANDLORDS.leDiscounted;
            } else {
              addOn.annualCost = ADD_ONS_ANNUAL_COST_LANDLORDS.le;
            }
          }

          //Apply dicount to RP if LE is selected
          if (addOn.code === "rp") {
            this.rentProtection.Value = addOn.isAdded;

            if (addOns.some((addOn) => addOn.isAdded && addOn.code === "le")) {
              addOn.annualCost = ADD_ONS_ANNUAL_COST_LANDLORDS.rpDiscounted;
            } else {
              addOn.annualCost = ADD_ONS_ANNUAL_COST_LANDLORDS.rp;
            }
          }
          if (addOn.code === "ad") this.accidentalDamage.Value = addOn.isAdded;
        });
      },
      deep: true,
    },
  },
  mounted() {
    if (this.productGroup === LANDLORDS) {
      this.legalExpenses = this.questionMap.LegalExpenses;
      const leAddOn = this.addOns.find((addOn) => addOn.code === "le");
      leAddOn.isAdded = this.legalExpenses.Value;

      this.landlordsEmergency = this.questionMap.LandlordsEmergency;
      const heAddOn = this.addOns.find((addOn) => addOn.code === "he");
      heAddOn.isAdded = this.landlordsEmergency.Value;

      this.rentProtection = this.questionMap.RentGuarantee;
      const rpAddOn = this.addOns.find((addOn) => addOn.code === "rp");
      rpAddOn.isAdded = this.rentProtection.Value;

      this.addOns.forEach((addOn) => {
        // when retrieving a quote convert "Boolean Strings" to Booleans
        addOn.isAdded = convertToBool(addOn.isAdded);

        if ((addOn.code === "le" || addOn.code === "rp") && addOn.isAdded)
          this.isEligibilityConfirmed = true;
      });

      // Restore the user's previous answer so it isn't lost when the component is recreated
      const storedValue = this.questionMap.NewLetOrProductTransfer?.Value;
      if (storedValue === true || storedValue === false) {
        this.lastNewLetAnswer = storedValue;
      }
    }
  },
  methods: {
    ...mapMutations(DIALOG_MODULE, ["showModal"]),

    showWhatsIncludedDialog(option) {
      if (option.code === "he") this.isShowWhatsIncludedDialogHE = true;
      if (option.code === "le") this.isShowWhatsIncludedDialogLE = true;
      if (option.code === "rp") this.isShowWhatsIncludedDialogRP = true;
    },
    handleShowDialog(option) {
      if (option.dialogComponentName) {
        this.showModal({
          dialogComponentName: option.dialogComponentName,
          customClass: "pb-10 dialog-contents__wrapper",
          dialogWidth: option.dialogWidth,
        });
      } else {
        this.showWhatsIncludedDialog(option);
      }
    },
    handleAddOption(option) {
      if (this.isEligibilityConfirmed && this.lastNewLetAnswer !== null) {
        option.isAdded = !option.isAdded;
        if (option.isAdded) {
          this.questionMap.AddOnEligibility.Value = true;
          this.storeNewLetAnswer(this.lastNewLetAnswer);
        }
        this.clearAddonEligibilityIfNoneSelected();
        return;
      }
      this.pendingOption = option;
      this.isShowEligibilityDrawer = true;
      this.$emit("setDrawerRentProtectionLegalExpensesEligibilityCheck", true);
    },
    onEligibilityConfirmed(res) {
      this.isEligibilityConfirmed = true;
      this.lastNewLetAnswer = res.newLetOrProductTransfer;
      this.questionMap.AddOnEligibility.Value = true;
      if (this.pendingOption) {
        this.pendingOption.isAdded = true;
      }
      this.storeNewLetAnswer(res.newLetOrProductTransfer);
      this.pendingOption = null;
      this.$emit("setDrawerRentProtectionLegalExpensesEligibilityCheck", false);
    },
    onEligibilityCancelled() {
      if (this.pendingOption) {
        this.pendingOption.isAdded = false;
      }
      this.pendingOption = null;
      this.$emit("setDrawerRentProtectionLegalExpensesEligibilityCheck", false);
    },
    toggleOption(option) {
      if (this.handleToggleExternal) {
        this.$emit("toggleOption", option);
        return;
      }
      if (this.productGroup !== LANDLORDS) {
        option.isAdded = !option.isAdded;
        return;
      }

      if (option.code === "le" || option.code === "rp") {
        this.handleAddOption(option);
      } else {
        option.isAdded = !option.isAdded;
      }
    },
    storeNewLetAnswer(value) {
      this.questionMap.NewLetOrProductTransfer.Value = value;
      this.questionMap.NewLetOrProductTransfer.Source = "User";
    },
    clearAddonEligibilityIfNoneSelected() {
      const hasRpOrLe = this.addOns.some(
        (addOn) => (addOn.code === "le" || addOn.code === "rp") && addOn.isAdded
      );
      if (!hasRpOrLe) {
        this.questionMap.AddOnEligibility.Value = "";
        this.questionMap.NewLetOrProductTransfer.Value = "";
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/scss/AppComponents/_DrawerCustomizeCover.scss";
</style>
